import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { api, formatErr, cx } from "../lib/api";
import { useI18n } from "../lib/i18n";

const STATUS_OPTIONS = ["draft", "comingSoon", "active", "hidden", "archived"];
const CURRENCIES = ["EUR", "USD", "GBP", "AED", "MXN", "IDR"];

const blank = {
  name: "",
  country: "",
  region: "",
  flag_emoji: "",
  status: "comingSoon",
  hero_title: "",
  hero_subtitle: "",
  hero_media_url: "",
  description: "",
  default_currency: "EUR",
  timezone: "UTC",
  sort_order: 99,
};

/**
 * Admin destination create / edit modal.
 *
 * Props:
 * - mode: "create" | "edit"
 * - destination: existing destination object when editing
 * - onClose(refresh: boolean): closes; refresh=true => parent should reload
 */
export default function DestinationFormModal({ mode, destination, onClose }) {
  const { t } = useI18n();
  const isEdit = mode === "edit";
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isEdit && destination) {
      setForm({
        name: destination.name || "",
        country: destination.country || "",
        region: destination.region || "",
        flag_emoji: destination.flag_emoji || "",
        status: destination.status || "comingSoon",
        hero_title: destination.hero_title || "",
        hero_subtitle: destination.hero_subtitle || "",
        hero_media_url: destination.hero_media_url || "",
        description: destination.description || "",
        default_currency: destination.default_currency || "EUR",
        timezone: destination.timezone || "UTC",
        sort_order: destination.sort_order ?? 99,
      });
    } else {
      setForm(blank);
    }
  }, [mode, destination, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const payload = {
      name: form.name.trim(),
      country: form.country.trim(),
      region: form.region.trim() || null,
      flag_emoji: form.flag_emoji.trim() || null,
      status: form.status,
      hero_title: form.hero_title.trim() || form.name.trim(),
      hero_subtitle: form.hero_subtitle.trim() || null,
      hero_media_url: form.hero_media_url.trim() || null,
      description: form.description.trim() || null,
      default_currency: form.default_currency,
      timezone: form.timezone.trim() || "UTC",
      sort_order: Number(form.sort_order),
    };
    try {
      if (isEdit) {
        await api.patch(`/destinations/${destination.id}`, payload);
      } else {
        await api.post(`/destinations`, payload);
      }
      onClose(true);
    } catch (e) {
      setError(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center p-0 md:p-6 bg-ink-deep/70 backdrop-blur-sm" data-testid="destination-form-modal">
      <div className="w-full md:max-w-3xl max-h-[95svh] overflow-y-auto modal-panel">
        <div className="sticky top-0 flex items-center justify-between px-6 py-4 border-b hairline glass-solid z-10">
          <div>
            <div className="eyebrow">{isEdit ? t("admin_destinations.edit_title", "Edit destination") : t("admin_destinations.create_title", "New destination")}</div>
            <div className="font-serif-display text-xl mt-1">{form.name || t("admin_destinations.name_placeholder", "e.g. Mykonos")}</div>
          </div>
          <button onClick={() => onClose(false)} className="text-muted hover:text-bone" aria-label="Close" data-testid="destination-form-close">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5" data-testid="destination-form">
          <div className="grid md:grid-cols-3 gap-4">
            <Field label={t("admin_destinations.field_name", "Name")}>
              <input required maxLength={80} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={t("admin_destinations.name_placeholder", "e.g. Mykonos")} className="input-bare" data-testid="df-name" />
            </Field>
            <Field label={t("admin_destinations.field_country", "Country")}>
              <input required value={form.country} onChange={(e) => set("country", e.target.value)} placeholder="Greece" className="input-bare" data-testid="df-country" />
            </Field>
            <Field label={t("admin_destinations.field_flag", "Flag emoji")}>
              <input maxLength={8} value={form.flag_emoji} onChange={(e) => set("flag_emoji", e.target.value)} placeholder="🇬🇷" className="input-bare" data-testid="df-flag" />
            </Field>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label={t("admin_destinations.field_region", "Region")}>
              <input value={form.region} onChange={(e) => set("region", e.target.value)} placeholder="Cyclades" className="input-bare" data-testid="df-region" />
            </Field>
            <Field label={t("admin_destinations.field_status", "Status")}>
              <select value={form.status} onChange={(e) => set("status", e.target.value)} className="input-bare" data-testid="df-status">
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </Field>
          </div>

          <Field label={t("admin_destinations.field_hero_title", "Hero title")}>
            <input value={form.hero_title} onChange={(e) => set("hero_title", e.target.value)} placeholder={form.name || "Mykonos."} className="input-bare" data-testid="df-hero-title" />
          </Field>

          <Field label={t("admin_destinations.field_hero_subtitle", "Hero subtitle")}>
            <input value={form.hero_subtitle} onChange={(e) => set("hero_subtitle", e.target.value)} placeholder="Aegean nightlife & cliffside villas." className="input-bare" data-testid="df-hero-subtitle" />
          </Field>

          <Field label={t("admin_destinations.field_hero_media", "Hero image URL")}>
            <input value={form.hero_media_url} onChange={(e) => set("hero_media_url", e.target.value)} placeholder="https://images.unsplash.com/..." className="input-bare font-mono text-xs" data-testid="df-hero-media" />
            {form.hero_media_url && (
              <img src={form.hero_media_url} alt="" className="mt-2 rounded-lg w-full max-h-32 object-cover border hairline" onError={(ev) => { ev.currentTarget.style.display = "none"; }} />
            )}
          </Field>

          <Field label={t("admin_destinations.field_description", "Description")}>
            <textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="input-bare resize-y" data-testid="df-description" />
          </Field>

          <div className="grid md:grid-cols-3 gap-4">
            <Field label={t("admin_destinations.field_currency", "Default currency")}>
              <select value={form.default_currency} onChange={(e) => set("default_currency", e.target.value)} className="input-bare" data-testid="df-currency">
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label={t("admin_destinations.field_timezone", "Timezone")}>
              <input value={form.timezone} onChange={(e) => set("timezone", e.target.value)} placeholder="Europe/Athens" className="input-bare" data-testid="df-timezone" />
            </Field>
            <Field label={t("admin_destinations.field_sort_order", "Sort order")}>
              <input type="number" min="0" step="1" value={form.sort_order} onChange={(e) => set("sort_order", e.target.value)} className="input-bare" data-testid="df-sort-order" />
            </Field>
          </div>

          {error && <div className="text-rose-300 text-sm" data-testid="df-error">{error}</div>}

          <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
            <button type="button" onClick={() => onClose(false)} className="btn btn-ghost" data-testid="df-cancel">
              {t("provider_dash.cancel", "Cancel")}
            </button>
            <button type="submit" disabled={busy} className={cx("btn btn-primary", busy && "opacity-60")} data-testid="df-submit">
              {busy
                ? isEdit
                  ? t("provider_dash.saving_service", "Saving…")
                  : t("provider_dash.creating_service", "Creating…")
                : t("provider_dash.save_service", "Save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="label-tiny block mb-1.5">{label}</span>
      {children}
    </label>
  );
}
